/**
 * Date, number and list formatting.
 *
 * Every function takes an **explicit** locale argument. Design doc §5.6 stores
 * locale per content variant, so a helper that quietly read the UI language
 * would format a `zh-CN` variant's dates in `en-CA` whenever the viewer's
 * chrome happened to be English.
 */

/** Absolute timestamp in the workspace's presentation timezone. */
export function formatDateTime(locale: string, iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function formatDate(locale: string, iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

/** "in 3 days" / "2 hours ago", for expiry countdowns. */
export function formatRelative(locale: string, iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const units: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return formatter.format(seconds, 'second');
}

export function formatList(locale: string, items: readonly string[]): string {
  return new Intl.ListFormat(locale, { style: 'short', type: 'conjunction' }).format(items);
}

/** Shortened hash for display. The full value stays in `title`/`aria-label`. */
export function shortHash(hash: string): string {
  return hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : hash;
}

/** A currency amount, formatted for the locale. Falls back to the plain code on an unknown currency. */
export function formatMoney(locale: string, amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale).format(amount)} ${currency}`;
  }
}

/** A plain number, grouped for the locale. */
export function formatNumber(locale: string, value: number): string {
  return new Intl.NumberFormat(locale).format(value);
}
